import { Flex, Text, Tooltip } from '@chakra-ui/react';
import { faEdit } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonIcon } from '@morressier/alembic';
import { ISubmissionContext } from '@publishing-engine/domain/models';
import { ReactNode, useState } from 'react';
import { EditGeneralInfo } from './EditGeneralInfo';
import { GeneralInfoEditableFieldConfig } from './generalInfoConfig';

const GeneralInfoEditableSection = ({
  label,
  value,
  journal,
  fieldConfig,
  layout = 'stacked'
}: {
  label: string;
  value: ReactNode;
  journal: ISubmissionContext;
  fieldConfig?: GeneralInfoEditableFieldConfig;
  /**
   * `stacked` is the General tab's label-over-value block. `inline` puts the edit
   * button beside the value and drops the label, for callers rendering the field
   * in a row that already carries its own heading — the label is not lost, it is
   * the one the edit modal shows.
   */
  layout?: 'stacked' | 'inline';
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isEditable = fieldConfig?.isEditable ?? true;
  const disabledTooltip = fieldConfig?.disabledTooltip;

  /**
   * A disabled button fires no hover events of its own, so the icon is wrapped to
   * let the tooltip still explain the lock — and only a locked field gets one.
   */
  const editButton = fieldConfig ? (
    <Tooltip
      shouldWrapChildren
      isDisabled={isEditable}
      hasArrow
      label={disabledTooltip ?? ''}
      fontSize="md"
      placement="top"
    >
      <ButtonIcon
        onClick={() => setIsOpen(true)}
        ariaLabel={`edit-${fieldConfig.key}`}
        icon={<FontAwesomeIcon icon={faEdit} />}
        disabled={!isEditable}
      />
    </Tooltip>
  ) : null;

  return (
    <>
      {layout === 'inline' ? (
        <Flex alignItems="center" gap={2}>
          {value}
          {editButton}
        </Flex>
      ) : (
        <Flex direction="column" gap={2}>
          <Flex alignItems="center" gap={2}>
            <Text textStyle="headingItem">{label}</Text>
            {editButton}
          </Flex>
          {value}
        </Flex>
      )}

      {fieldConfig && isOpen ? (
        <EditGeneralInfo
          journal={journal}
          fieldConfig={fieldConfig}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
        />
      ) : null}
    </>
  );
};

export default GeneralInfoEditableSection;
